import { selectFieldStatesByNames } from '../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../store/applicationform/applicationFormTypes';
import { Question } from '../types/KycTypes';
import { useAppSelector } from './hooks';

const useValidation = (fieldNames: string[], pageIndexQuestions?: Question[]): IFieldState[] => {
    const fieldStates = useAppSelector(selectFieldStatesByNames)(fieldNames);

    const errorMessages: Record<string, string> = {
        applicantEmailAddress: 'validation.email.required',
        applicantMobileNumber: 'validation.phone.required',
        applicantMaritalStatus: 'validation.option.required',
        applicantHabitationForm: 'validation.option.required',
        applicantCitizenship: 'validation.option.required',
        applicantResidentPermit: 'validation.option.required',
        applicantAncestralHomeland: 'validation.option.required',
        applicantInDenmarkSinceYear: 'validation.date.required',
        applicantResidentPermitValidDate: 'validation.date.required',
        applicantAccomodationCost: 'validation.rent-share.required',
        applicantEmploymentType: 'validation.employment.required',
        applicantEmployedSince: 'validation.date.required',
        applicantGovernmentId: 'validation.government.id.required',
        applicantYearlyGrossIncome: 'validation.income.required',
        applicantMortgageDebt: 'validation.mortage.required',
        applicantMortgageMonthlyCost: 'validation.mortage.monthly-cost.required',
        householdLivingExpenses: 'validation.household-living-expenses.required',
        householdLoanExpenses: 'validation.household-loan-expenses.required',
        applicantNumberOfCars: 'validation.applicant-number-of-cars.required',
        applicantNewDebtThisYearToggle: 'validation.option.required',
        applicantNewDebtThisYear: 'validation.new-debt-this-year.required',
        applicantShareOfExpenses: 'validation.share-of-expenses.required',
        applicantHabitationYear: 'validation.habitation-year.required',
        applicantCarLoanSum: 'validation.car-loan.required',
        applicantCarLoanMonthlyCost: 'validation.car-loan.monthly-cost.required',
        applicantCarLeasingMonthlyCost: 'validation.car-leasing.monthly-cost.required',
        applicantStudentLoanSum: 'validation.student-loan.required',
        applicantStudentLoanMonthlyCost: 'validation.student-loan.monthly-cost.required',
        applicantCreditRemarks: 'validation.option.required',
        applicantDankort: 'validation.option.required',
        coapplicantToggle: 'validation.option.required',
        coapplicantGovernmentId: 'validation.co-applicant.government.id.required',
        coapplicantEmailAddress: 'validation.co-applicant.email.required',
        coapplicantMobileNumber: 'validation.co-applicant.phone.required',
        payoutAccountNumber: 'validation.account-number.required',
        coapplicantMaritalStatus: 'validation.option.required',
        coapplicantEmploymentType: 'validation.employment.required',
        coapplicantEmployedSince: 'validation.date.required',
        coapplicantYearlyGrossIncome: 'validation.income.required',
        coapplicantMortgageDebt: 'validation.mortage.required',
        coapplicantMortgageMonthlyCost: 'validation.mortage.monthly-cost.required',
        coapplicantCarLoanSum: 'validation.car-loan.required',
        coapplicantCarLoanMonthlyCost: 'validation.car-loan.monthly-cost.required',
        coapplicantCarLeasingMonthlyCost: 'validation.car-leasing.monthly-cost.required',
        coapplicantStudentLoanSum: 'validation.student-loan.required',
        coapplicantStudentLoanMonthlyCost: 'validation.student-loan.monthly-cost.required',
        coapplicantNewDebtThisYearToggle: 'validation.option.required',
        coapplicantNewDebtThisYear: 'validation.new-debt-this-year.required',
        coapplicantConsolidationToggle: 'validation.option.required',
    };

    const getErrorMessage = (fieldName: string): string => {
        const extractQuestionId = (fieldName: string): string | null => fieldName.split('_').pop() || null;
        const questionId = extractQuestionId(fieldName);
        const relatedQuestion = pageIndexQuestions?.find((question) => question.name.toString() === questionId);

        if (relatedQuestion) {
            switch (relatedQuestion.type) {
                case 'SINGLE_SELECTION':
                    return 'validation.option.required';
                case 'MULTIPLE_SELECTION':
                    return 'validation.multiple-option.required';
                case 'FREE_TEXT':
                    return 'validation.required';
            }
        }

        if (fieldName.startsWith('CONSENT:')) {
            return 'validation.consent.required';
        } else if (fieldName.endsWith(';accountNumber')) {
            return 'validation.account-number.required';
        } else if (fieldName.endsWith(';creditorName')) {
            return 'validation.creditor-name.required';
        }
        return errorMessages[fieldName] || 'validation.required';
    };

    const missingFieldStates: IFieldState[] = fieldNames
        .filter(
            (fieldName) =>
                !fieldStates.find(
                    (fieldState) =>
                        fieldState.fieldName === fieldName &&
                        fieldState.value !== undefined &&
                        fieldState.value !== '' &&
                        !(Array.isArray(fieldState.value) && fieldState.value.length === 0),
                ),
        )
        .map((fieldName) => ({
            errorMessage: getErrorMessage(fieldName),
            fieldName: fieldName,
            hasError: true,
            value: undefined,
        }));

    return [
        ...missingFieldStates,
        ...fieldStates.filter(
            (field) =>
                (field.hasError || !field.value) &&
                !missingFieldStates.find((fieldState) => fieldState.fieldName === field.fieldName),
        ),
    ];
};

export default useValidation;
